import React, { useEffect, useState } from "react";

/**
 * Generic read-only folder browser for the Exercise Wizard.
 * Supports hierarchical (multi-level breadcrumb) and flat (single-level tabs) modes.
 * No CRUD, no drag-drop — purely navigation + selection.
 */

type FolderBrowserProps<TContainer, TItem> = {
  containers: TContainer[];
  items: TItem[];
  // Accessors
  getContainerId: (c: TContainer) => number;
  getContainerName: (c: TContainer) => string;
  getContainerColor: (c: TContainer) => string | undefined;
  getContainerParentId: (c: TContainer) => number | null;
  getItemId: (item: TItem) => string;
  getItemName: (item: TItem) => string;
  getItemContainerId: (item: TItem) => number | null;
  matchesSearch: (item: TItem, query: string) => boolean;
  // Selection
  selectedItemId?: string | null;
  onSelectItem?: (id: string) => void;
  multiSelect?: boolean;
  selectedIds?: string[];
  onToggleItem?: (id: string) => void;
  // Rendering
  renderItemCard: (
    item: TItem,
    isSelected: boolean,
    onSelect: () => void
  ) => React.ReactNode;
  // Options
  hierarchical?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
};

export function FolderBrowser<TContainer, TItem>({
  containers,
  items,
  getContainerId,
  getContainerName,
  getContainerColor,
  getContainerParentId,
  getItemId,
  getItemName,
  getItemContainerId,
  matchesSearch,
  selectedItemId,
  onSelectItem,
  multiSelect,
  selectedIds,
  onToggleItem,
  renderItemCard,
  hierarchical = true,
  searchPlaceholder = "Cerca...",
  emptyMessage = "Nessun elemento.",
}: FolderBrowserProps<TContainer, TItem>) {
  const [path, setPath] = useState<number[]>([]);
  const [search, setSearch] = useState("");

  // Reset search on navigation
  useEffect(() => setSearch(""), [path]);

  // Validate path when containers change
  useEffect(() => {
    const validUpTo = path.findIndex(
      (id) => !containers.some((c) => getContainerId(c) === id)
    );
    if (validUpTo !== -1) setPath((p) => p.slice(0, validUpTo));
  }, [containers, path, getContainerId]);

  // Navigation helpers
  const childrenOf = (parentId: number) =>
    containers.filter((c) => getContainerParentId(c) === parentId);

  const itemsIn = (containerId: number) =>
    items.filter((item) => getItemContainerId(item) === containerId);

  const orphanItems = items.filter(
    (item) =>
      getItemContainerId(item) == null ||
      !containers.some((c) => getContainerId(c) === getItemContainerId(item))
  );

  const currentContainerId = path.length ? path[path.length - 1] : null;

  const q = search.trim().toLowerCase();
  const searching = q.length > 0;

  // Build path to a container (for search results click)
  const pathTo = (c: TContainer): number[] => {
    const ids: number[] = [];
    let cur: TContainer | undefined = c;
    const guard = new Set<number>();
    while (cur && !guard.has(getContainerId(cur))) {
      guard.add(getContainerId(cur));
      ids.unshift(getContainerId(cur));
      const pid = getContainerParentId(cur);
      cur =
        pid != null
          ? containers.find((x) => getContainerId(x) === pid)
          : undefined;
    }
    return ids;
  };

  const openFolder = (c: TContainer) => setPath(pathTo(c));

  // What to show
  const foldersToShow = searching
    ? containers.filter(
        (c) =>
          getContainerName(c).toLowerCase().includes(q) ||
          itemsIn(getContainerId(c)).some((item) => matchesSearch(item, q))
      )
    : hierarchical
    ? currentContainerId == null
      ? containers.filter((c) => getContainerParentId(c) == null)
      : childrenOf(currentContainerId)
    : containers;

  const itemsToShow = searching
    ? items.filter((item) => matchesSearch(item, q))
    : hierarchical
    ? currentContainerId == null
      ? orphanItems
      : itemsIn(currentContainerId)
    : currentContainerId == null
    ? orphanItems
    : itemsIn(currentContainerId);

  const isSelected = (item: TItem) => {
    const id = getItemId(item);
    if (multiSelect && selectedIds) return selectedIds.includes(id);
    return selectedItemId === id;
  };

  const handleSelect = (item: TItem) => {
    const id = getItemId(item);
    if (multiSelect && onToggleItem) {
      onToggleItem(id);
    } else if (onSelectItem) {
      onSelectItem(id);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Breadcrumb (hierarchical) or Tabs (flat) */}
      {hierarchical ? (
        <div className="flex items-center gap-2 mb-3 text-sm flex-wrap">
          <button
            onClick={() => setPath([])}
            className={`hover:underline ${
              path.length === 0
                ? "text-gray-700 dark:text-gray-300 font-medium"
                : "text-blue-600 dark:text-blue-400"
            }`}
          >
            Tutti
          </button>
          {path.map((id, i) => {
            const c = containers.find((x) => getContainerId(x) === id);
            const isLast = i === path.length - 1;
            return (
              <React.Fragment key={id}>
                <span className="text-gray-400">/</span>
                {isLast ? (
                  <span className="text-gray-700 dark:text-gray-300 font-medium">
                    {c ? getContainerName(c) : "?"}
                  </span>
                ) : (
                  <button
                    onClick={() => setPath(path.slice(0, i + 1))}
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {c ? getContainerName(c) : "?"}
                  </button>
                )}
              </React.Fragment>
            );
          })}
        </div>
      ) : (
        /* Flat mode: tab buttons for each container */
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <button
            onClick={() => setPath([])}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              currentContainerId == null
                ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400 font-medium"
                : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
            }`}
          >
            Tutti
          </button>
          {containers.map((c) => {
            const cId = getContainerId(c);
            const active = currentContainerId === cId;
            const color = getContainerColor(c);
            return (
              <button
                key={cId}
                onClick={() => setPath([cId])}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  active
                    ? "font-medium"
                    : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
                }`}
                style={
                  active && color
                    ? {
                        backgroundColor: color + "20",
                        color: color,
                      }
                    : undefined
                }
              >
                {getContainerName(c)}
              </button>
            );
          })}
        </div>
      )}

      {/* Search */}
      <div className="relative mb-3">
        <input
          type="text"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="block w-full py-2 px-4 text-sm text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 text-lg leading-none"
          >
            &times;
          </button>
        )}
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {/* Folder cards */}
          {!searching &&
            foldersToShow.map((c) => {
              const cId = getContainerId(c);
              const color = getContainerColor(c);
              const subCount = hierarchical ? childrenOf(cId).length : 0;
              const exCount = itemsIn(cId).length;
              return (
                <button
                  key={`f-${cId}`}
                  onClick={() => openFolder(c)}
                  className="flex items-center gap-3 p-4 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left w-full"
                >
                  {/* Folder icon with color */}
                  <div
                    className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{
                      backgroundColor: (color || "#6B7280") + "20",
                    }}
                  >
                    <svg
                      className="w-5 h-5"
                      style={{ color: color || "#6B7280" }}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {getContainerName(c)}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      {exCount} element{exCount !== 1 ? "i" : "o"}
                      {subCount > 0 && ` · ${subCount} cartell${subCount !== 1 ? "e" : "a"}`}
                    </p>
                  </div>
                  <svg
                    className="w-4 h-4 text-gray-400 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              );
            })}

          {/* Searching: show matching folders as navigable */}
          {searching &&
            foldersToShow.map((c) => {
              const cId = getContainerId(c);
              const color = getContainerColor(c);
              return (
                <button
                  key={`sf-${cId}`}
                  onClick={() => {
                    setSearch("");
                    openFolder(c);
                  }}
                  className="flex items-center gap-3 p-4 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left w-full"
                >
                  <div
                    className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: (color || "#6B7280") + "20" }}
                  >
                    <svg
                      className="w-5 h-5"
                      style={{ color: color || "#6B7280" }}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {getContainerName(c)}
                    </p>
                  </div>
                </button>
              );
            })}

          {/* Item cards */}
          {itemsToShow.map((item) => (
            <React.Fragment key={`i-${getItemId(item)}`}>
              {renderItemCard(item, isSelected(item), () =>
                handleSelect(item)
              )}
            </React.Fragment>
          ))}

          {/* Empty state */}
          {foldersToShow.length === 0 && itemsToShow.length === 0 && (
            <div className="col-span-full text-center text-gray-400 dark:text-gray-500 py-8">
              {searching ? "Nessun risultato per la ricerca." : emptyMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
